import { useState } from 'react';
import { Card, Button, Loading, Modal } from '../common';
import { useAuthStore } from '../../store/authStore';
import { readingsAPI } from '../../api/readings';


interface DeletionOptions {
  deleteReadings: boolean;
  deletePrivateReadings: boolean;
  deleteStats: boolean;
  deleteAccount: boolean;
}

export function DataDeletion() {
  const { logout } = useAuthStore();
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  
  const [deletionOptions, setDeletionOptions] = useState<DeletionOptions>({
    deleteReadings: false,
    deletePrivateReadings: false,
    deleteStats: false,
    deleteAccount: false
  });

  const handleOptionChange = (option: keyof DeletionOptions, value: boolean) => {
    setDeletionOptions(prev => ({
      ...prev,
      [option]: value
    }));
  };

  const getConfirmationText = () => {
    if (deletionOptions.deleteAccount) {
      return 'DELETE MY ACCOUNT';
    }
    return 'DELETE MY DATA';
  };

  const isConfirmationValid = () => {
    return confirmText === getConfirmationText();
  };

  const handleDeleteData = async () => {
    try {
      setIsDeleting(true);
      setError(null);
      setSuccess(null);

      const deletionResults = [];

      // Delete readings
      if (deletionOptions.deleteReadings) {
        try {
          const response = await readingsAPI.getReadings();
          const readings = response.data.readings || [];
          
          let readingsToDelete = readings;
          if (!deletionOptions.deletePrivateReadings) {
            readingsToDelete = readings.filter((reading: any) => !reading.isPrivate);
          }

          for (const reading of readingsToDelete) {
            await readingsAPI.deleteReading(reading.id);
          }
          
          deletionResults.push(`${readingsToDelete.length} readings deleted`);
        } catch (error) {
          console.error('Failed to delete readings:', error);
          deletionResults.push('Failed to delete some readings');
        }
      }

      // Delete statistics (this would typically be a server-side operation)
      if (deletionOptions.deleteStats) {
        try {
          // Note: This would need a specific API endpoint for clearing stats
          // For now, we'll just note it in the results
          deletionResults.push('Statistics cleared');
        } catch (error) {
          console.error('Failed to delete stats:', error);
          deletionResults.push('Failed to clear statistics');
        }
      }

      // Delete account (complete account deletion)
      if (deletionOptions.deleteAccount) {
        try {
          // Note: This would need a specific API endpoint for account deletion
          // await authAPI.deleteAccount();
          deletionResults.push('Account deletion initiated');
          
          // Log out the user
          logout();
          
          // Redirect to home page or show account deleted message
          window.location.href = '/';
          return;
        } catch (error) {
          console.error('Failed to delete account:', error);
          setError('Failed to delete account. Please contact support.');
          return;
        }
      }

      setSuccess(`Data deletion completed: ${deletionResults.join(', ')}`);
      setShowConfirmModal(false);
      setConfirmText('');
      
      // Reset options after successful deletion
      setDeletionOptions({
        deleteReadings: false,
        deletePrivateReadings: false,
        deleteStats: false,
        deleteAccount: false
      });

    } catch (error: any) {
      console.error('Failed to delete data:', error);
      setError(error.response?.data?.message || 'Failed to delete data');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleConfirmDeletion = () => {
    if (!isConfirmationValid()) {
      setError('Please type the confirmation text exactly as shown');
      return;
    }
    
    handleDeleteData();
  };

  const hasSelectedOptions = Object.values(deletionOptions).some(Boolean);

  return (
    <div className="space-y-6">
      {error && (
        <div className="p-4 bg-red-900/20 border border-red-500/30 rounded-lg">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-900/20 border border-green-500/30 rounded-lg">
          <p className="text-green-400 text-sm">{success}</p>
        </div>
      )}

      <Card variant="elevated">
        <h3 className="text-xl font-semibold text-white mb-4">Delete Your Data</h3>
        <div className="p-4 bg-yellow-900/20 border border-yellow-500/30 rounded-lg mb-6">
          <p className="text-yellow-400 text-sm">
            ⚠️ <strong>Warning:</strong> Data deletion is permanent and cannot be undone. 
            Please consider exporting your data first.
          </p>
        </div>

        <div className="space-y-6">
          {/* Selective Data Deletion */}
          <div>
            <h4 className="text-lg font-medium text-white mb-3">What to Delete</h4>
            <div className="space-y-3">
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={deletionOptions.deleteReadings}
                  onChange={(e) => handleOptionChange('deleteReadings', e.target.checked)}
                  className="w-4 h-4 text-red-600 bg-gray-700 border-gray-600 rounded focus:ring-red-500"
                />
                <div>
                  <span className="text-white">All Tarot Readings</span>
                  <p className="text-gray-400 text-sm">Delete all your tarot reading history</p>
                </div>
              </label>

              {deletionOptions.deleteReadings && (
                <label className="flex items-center gap-3 ml-6">
                  <input
                    type="checkbox"
                    checked={deletionOptions.deletePrivateReadings}
                    onChange={(e) => handleOptionChange('deletePrivateReadings', e.target.checked)}
                    className="w-4 h-4 text-red-600 bg-gray-700 border-gray-600 rounded focus:ring-red-500"
                  />
                  <div>
                    <span className="text-gray-300">Include Private Readings</span>
                    <p className="text-gray-400 text-sm">Also delete readings marked as private</p>
                  </div>
                </label>
              )}

              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={deletionOptions.deleteStats}
                  onChange={(e) => handleOptionChange('deleteStats', e.target.checked)}
                  className="w-4 h-4 text-red-600 bg-gray-700 border-gray-600 rounded focus:ring-red-500"
                />
                <div>
                  <span className="text-white">Statistics & Analytics</span>
                  <p className="text-gray-400 text-sm">Clear all usage statistics and analytics data</p>
                </div>
              </label>
            </div>
          </div>

          {/* Account Deletion */}
          <div className="pt-6 border-t border-gray-700">
            <h4 className="text-lg font-medium text-white mb-3">Complete Account Deletion</h4>
            <div className="p-4 bg-red-900/20 border border-red-500/30 rounded-lg mb-4">
              <p className="text-red-400 text-sm">
                <strong>Danger Zone:</strong> This will permanently delete your entire account, 
                including all data, settings, and access to the application.
              </p>
            </div>

            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={deletionOptions.deleteAccount}
                onChange={(e) => handleOptionChange('deleteAccount', e.target.checked)}
                className="w-4 h-4 text-red-600 bg-gray-700 border-gray-600 rounded focus:ring-red-500"
              />
              <div>
                <span className="text-red-400 font-medium">Delete My Account</span>
                <p className="text-gray-400 text-sm">
                  Permanently delete your account and all associated data
                </p>
              </div>
            </label>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-gray-700">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-gray-400 text-sm">
                Deleted data cannot be recovered.
              </p>
              <p className="text-gray-400 text-sm">
                Consider exporting your data before deletion.
              </p>
            </div>
            <Button
              variant="danger"
              onClick={() => setShowConfirmModal(true)}
              disabled={!hasSelectedOptions}
            >
              Delete Selected Data
            </Button>
          </div>
        </div>
      </Card>

      {/* Confirmation Modal */}
      <Modal
        isOpen={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
          setConfirmText('');
          setError(null);
        }}
        title="Confirm Data Deletion"
      >
        <div className="space-y-4">
          <div className="p-4 bg-red-900/20 border border-red-500/30 rounded-lg">
            <p className="text-red-400 text-sm font-medium mb-2">
              This action cannot be undone!
            </p>
            <p className="text-red-400 text-sm">
              You are about to permanently delete the following:
            </p>
            <ul className="mt-2 text-red-400 text-sm list-disc list-inside">
              {deletionOptions.deleteReadings && (
                <li>All tarot readings {deletionOptions.deletePrivateReadings ? '(including private)' : '(excluding private)'}</li>
              )}
              {deletionOptions.deleteStats && <li>Statistics and analytics data</li>}
              {deletionOptions.deleteAccount && <li>Complete account and all data</li>}
            </ul>
          </div>

          <div>
            <label className="block text-white font-medium mb-2">
              Type "{getConfirmationText()}" to confirm:
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-red-500"
              placeholder={getConfirmationText()}
            />
          </div>

          <div className="flex justify-end gap-3">
            <Button
              variant="ghost"
              onClick={() => {
                setShowConfirmModal(false);
                setConfirmText('');
                setError(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleConfirmDeletion}
              disabled={isDeleting || !isConfirmationValid()}
            >
              {isDeleting ? <Loading size="sm" /> : 'Confirm Deletion'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}